import React from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Grid,
  TextField,
  Alert,
  Autocomplete,
  Chip,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

export const FilterSection = ({
  filterData,
  onFilterChange,
  yearOptions,
  monthOptions,
  companyOptions,
  branches,
  products,
  isLoadingBranches,
  isLoadingProducts,
  isLoadingFilterMode,
  branchError,
  productError,
}) => {
  const commonMenuProps = {
    PaperProps: {
      sx: {
        width: "auto",
        maxHeight: 300,
        "& .MuiMenuItem-root": {
          padding: "8px 16px",
          minHeight: "32px",
        },
      },
    },
  };

  const [dateError, setDateError] = React.useState(null);

  const handleStartDateChange = (newValue) => {
    if (!newValue) {
      setDateError("Start date is required");
      return;
    }
    if (filterData.endDate && newValue > filterData.endDate) {
      setDateError("Start date cannot be after end date");
      return;
    }
    setDateError(null);
    onFilterChange("startDate", newValue);
  };

  const handleEndDateChange = (newValue) => {
    if (!newValue) {
      setDateError("End date is required");
      return;
    }
    if (filterData.startDate && newValue < filterData.startDate) {
      setDateError("End date cannot be before start date");
      return;
    }
    setDateError(null);
    onFilterChange("endDate", newValue);
  };

  const allOption = { label: "(ALL)", value: "ALL" };

  return (
    <Box sx={{ mb: 3, backgroundColor: "#f8fafc", p: 3, borderRadius: "12px" }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Autocomplete
            size="small"
            fullWidth
            options={companyOptions}
            value={
              companyOptions.find((opt) => opt.value === filterData.company) ||
              null
            }
            onChange={(e, newValue) =>
              onFilterChange("company", newValue ? newValue.value : "")
            }
            getOptionLabel={(option) => option.label || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Company"
                placeholder="Select a company"
              />
            )}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
            Branch (Multiple Selection)
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              multiple
              value={
                filterData.branch === "ALL"
                  ? ["ALL"]
                  : Array.isArray(filterData.branch)
                  ? filterData.branch
                  : [filterData.branch]
              }
              onChange={(e) => {
                const selectedValues = e.target.value;
                const lastSelected = selectedValues[selectedValues.length - 1];
                const prevBranch = filterData.branch;

                if (lastSelected === "ALL") {
                  onFilterChange("branch", "ALL");
                  return;
                }

                if (
                  prevBranch === "ALL" ||
                  (Array.isArray(prevBranch) && prevBranch.includes("ALL"))
                ) {
                  const newSelection = selectedValues.filter(
                    (val) => val !== "ALL"
                  );
                  onFilterChange("branch", newSelection);
                  return;
                }

                if (selectedValues.length === 0) {
                  onFilterChange("branch", "ALL");
                  return;
                }

                onFilterChange("branch", selectedValues);
              }}
              MenuProps={commonMenuProps}
              disabled={isLoadingBranches || !filterData.company}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => {
                    const option =
                      value === "ALL"
                        ? allOption
                        : branches.find((b) => b.value === value) || {
                            label: value,
                            value,
                          };
                    return (
                      <Chip
                        key={option.value}
                        label={option.label}
                        variant="outlined"
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
            >
              <MenuItem value="ALL">(ALL)</MenuItem>
              {branches.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
            Product (Multiple Selection)
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              multiple
              value={
                filterData.product === "ALL"
                  ? ["ALL"]
                  : Array.isArray(filterData.product)
                  ? filterData.product
                  : [filterData.product]
              }
              onChange={(e) => {
                const selectedValues = e.target.value;

                const lastSelected = selectedValues[selectedValues.length - 1];
                const prevProduct = filterData.product;

                if (lastSelected === "ALL") {
                  onFilterChange("product", "ALL");
                  return;
                }

                if (
                  prevProduct === "ALL" ||
                  (Array.isArray(prevProduct) && prevProduct.includes("ALL"))
                ) {
                  const newSelection = selectedValues.filter(
                    (val) => val !== "ALL"
                  );
                  onFilterChange("product", newSelection);
                  return;
                }

                if (selectedValues.length === 0) {
                  onFilterChange("product", "ALL");
                  return;
                }
                onFilterChange("product", selectedValues);
              }}
              MenuProps={commonMenuProps}
              disabled={isLoadingProducts || !filterData.company}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => {
                    const option =
                      value === "ALL"
                        ? allOption
                        : products.find((p) => p.value === value) || {
                            label: value,
                            value,
                          };
                    return (
                      <Chip
                        key={option.value}
                        label={option.label}
                        variant="outlined"
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
            >
              <MenuItem value="ALL">(ALL)</MenuItem>
              {products.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
            Filter Mode
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={filterData.filterMode}
              onChange={(e) => {
                setDateError(null);
                onFilterChange("filterMode", e.target.value);
              }}
              MenuProps={commonMenuProps}
              disabled={isLoadingFilterMode || !filterData.company}
            >
              {[
                { key: "year", value: "By Year", label: "By Year" },
                { key: "month", value: "By Month", label: "By Month" },
                { key: "date", value: "By Date", label: "By Date Range" },
              ].map((option) => (
                <MenuItem key={`filter-${option.key}`} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {filterData.filterMode === "By Year" && (
          <Grid item xs={4}>
            <Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
              Year
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={filterData.year}
                onChange={(e) => onFilterChange("year", e.target.value)}
                MenuProps={commonMenuProps}
                disabled={isLoadingFilterMode || !filterData.company}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {filterData.filterMode === "By Month" && (
          <>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
                Year
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={filterData.year}
                  onChange={(e) => onFilterChange("year", e.target.value)}
                  MenuProps={commonMenuProps}
                  disabled={isLoadingFilterMode || !filterData.company}
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year.toString()}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
                Month
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={filterData.month}
                  onChange={(e) => onFilterChange("month", e.target.value)}
                  MenuProps={commonMenuProps}
                  disabled={isLoadingFilterMode || !filterData.company}
                >
                  {monthOptions.map((month) => (
                    <MenuItem key={month} value={month}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {filterData.filterMode === "By Date" && (
          <Grid item xs={8}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" gap={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ mb: 0.5 }}>
                      Start Date
                    </Typography>
                    <DesktopDatePicker
                      value={filterData.startDate || null}
                      onChange={handleStartDateChange}
                      inputFormat="dd MMM yyyy"
                      disableMaskedInput
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          sx={{
                            "& .MuiInputBase-root": { height: "32px" },
                          }}
                          error={!!dateError && dateError.includes("Start")}
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ mb: 0.5 }}>
                      End Date
                    </Typography>
                    <DesktopDatePicker
                      value={filterData.endDate || null}
                      onChange={handleEndDateChange}
                      inputFormat="dd MMM yyyy"
                      disableMaskedInput
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          sx={{
                            "& .MuiInputBase-root": { height: "32px" },
                          }}
                          error={!!dateError && dateError.includes("End")}
                        />
                      )}
                    />
                  </Box>
                </Box>

                {dateError && (
                  <Alert severity="error" sx={{ py: 0.5 }}>
                    {dateError}
                  </Alert>
                )}
              </Box>
            </LocalizationProvider>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
